import { FEATURE_LAYERS_PROPERTIES } from '../constants';
import { WebMap } from '../constructors';
import type { MapSurface } from '../types';
import { MAIN_MAP_SURFACE } from '../types';
import { logDebug, logError, logInfo, logWarn } from '../../monitoring/log';
import {
    incrementContextLossCount,
    incrementContextRestoreCount,
    incrementMapErrorCount,
} from './map-diagnostic-context';

export type MapSource =
    | 'service'
    | 'sdk'
    | 'webgl'
    | 'layer'
    | 'user_action'
    | 'tracker'
    | 'lifecycle'
    | 'cache';

type MapLogParams = {
    source: MapSource;
    event: string;
    error?: Error;
    /** Which map the event is about, when the caller knows. Logged as `mapSurface`,
     * and determines `mapIsEmbedded`. */
    surface?: MapSurface;
    /** For callers that only know main vs embedded. Ignored when `surface` is given. */
    isEmbeddedMap?: boolean;
    extraData?: Record<string, unknown>;
};

function buildMapContext(
    { source, event, surface, isEmbeddedMap }: MapLogParams,
    extraData: Record<string, unknown> | undefined
) {
    return {
        domain: 'map',
        mapSource: source,
        mapEvent: event,
        mapIsEmbedded: surface ? surface !== MAIN_MAP_SURFACE : !!isEmbeddedMap,
        ...(surface ? { mapSurface: surface } : {}),
        ...extraData,
    };
}

function getMapLogMessage(source: MapSource, event: string) {
    return `map: ${source}.${event}`;
}

/**
 * Each map event is capped at {@link MAX_LOGS_PER_EVENT_PER_WINDOW} per page load per
 * {@link RATE_LIMIT_WINDOW_MS}, so one broken session can't drown the signal:
 * `layerview_create_error` once logged 112k events in a week, 94% of them from four
 * sessions. Dropped occurrences are counted, and the next log of that event that does
 * go out reports them as `suppressedSinceLastLog`.
 *
 * Episode events (`source: 'tracker'`) are uncapped: there is already exactly one per
 * episode, and they are the metric itself.
 */
const MAX_LOGS_PER_EVENT_PER_WINDOW = 20;
const RATE_LIMIT_WINDOW_MS = 60 * 60 * 1000;
const UNCAPPED_SOURCES: ReadonlySet<MapSource> = new Set<MapSource>(['tracker']);

type RateLimitBucket = { startedAtMs: number; logged: number; suppressed: number };
const rateLimitBuckets = new Map<string, RateLimitBucket>();

/**
 * Whether this occurrence of an event may be logged. Returns how many occurrences were
 * suppressed since the last one that went out, or `null` to suppress this one. Fails
 * open: a bug here must never cost us the log.
 */
function admitMapLog({ source, event, surface }: MapLogParams): number | null {
    if (UNCAPPED_SOURCES.has(source)) {
        return 0;
    }
    try {
        // Per surface, so a page cycling through preview maps can't use up the main
        // map's allowance for the same event.
        const key = surface ? `${source}.${event}.${surface}` : `${source}.${event}`;
        const now = Date.now();
        let bucket = rateLimitBuckets.get(key);
        if (!bucket || now - bucket.startedAtMs >= RATE_LIMIT_WINDOW_MS) {
            // The suppressed count carries over until a log actually reports it.
            bucket = { startedAtMs: now, logged: 0, suppressed: bucket?.suppressed ?? 0 };
            rateLimitBuckets.set(key, bucket);
        }
        if (bucket.logged >= MAX_LOGS_PER_EVENT_PER_WINDOW) {
            bucket.suppressed++;
            return null;
        }
        bucket.logged++;
        const suppressed = bucket.suppressed;
        bucket.suppressed = 0;
        return suppressed;
    } catch {
        return 0;
    }
}

/**
 * An Error standing in for a condition we detected — a lost WebGL context, a fatal SDK
 * state — rather than an exception we caught. A real stack here could only ever point
 * at this helper, and it was shipped twice per event (`error.stack` and `error_stack`):
 * up to 38% of the payload. Caught exceptions keep their real stacks.
 */
function conditionError(message: string): Error {
    const error = new Error(message);
    error.stack = message;
    return error;
}

/** Esri's cancellation of pending work, e.g. a layer query aborted because the view is
 * being destroyed. Not a failure. */
export function isAbortError(error: unknown): boolean {
    return (error as { name?: unknown } | null)?.name === 'AbortError';
}

type MapLogLevel = 'debug' | 'info' | 'warn';
const WRITE_AT_LEVEL = { debug: logDebug, info: logInfo, warn: logWarn };

function writeMapLog(level: MapLogLevel, params: Omit<MapLogParams, 'error'>) {
    const suppressed = admitMapLog(params);
    if (suppressed === null) {
        return;
    }
    WRITE_AT_LEVEL[level](
        getMapLogMessage(params.source, params.event),
        buildMapContext(params, withSuppressedCount(params.extraData, suppressed))
    );
}

function withSuppressedCount(
    extraData: Record<string, unknown> | undefined,
    suppressed: number
): Record<string, unknown> | undefined {
    return suppressed > 0 ? { ...extraData, suppressedSinceLastLog: suppressed } : extraData;
}

export function logMapError(params: MapLogParams) {
    // Counted before the rate limit: the counter is a fact about the session, not a log.
    incrementMapErrorCount();
    const suppressed = admitMapLog(params);
    if (suppressed === null) {
        return;
    }
    const message = getMapLogMessage(params.source, params.event);
    logError(
        params.error ?? conditionError(message),
        buildMapContext(params, withSuppressedCount(params.extraData, suppressed))
    );
}

export function logMapWarn(params: Omit<MapLogParams, 'error'>) {
    writeMapLog('warn', params);
}

export function logMapInfo(params: Omit<MapLogParams, 'error'>) {
    writeMapLog('info', params);
}

export function logMapDebug(params: Omit<MapLogParams, 'error'>) {
    writeMapLog('debug', params);
}

/**
 * Generates a letter key corresponding to the given index.
 * Keys start at A = 0, then proceed up until Z.
 * Numbers after 25 become AA, up to AZ, then on to BA, and so forth.
 * @param index The index
 * @returns The corresponding key
 */
export function getMapKey(index: number): string {
    const ordinateA = 'a'.charCodeAt(0);
    const ordinateZ = 'z'.charCodeAt(0);
    const len = ordinateZ - ordinateA + 1;

    let s = '';
    while (index >= 0) {
        s = String.fromCharCode((index % len) + ordinateA) + s;
        index = Math.floor(index / len) - 1;
    }
    return s;
}

export function getFeatureEntityIds(
    features: Array<{ attributes?: { entityId?: unknown } }>
): number[] {
    return features
        .map((feature) => feature.attributes?.entityId)
        .filter((entityId): entityId is number => typeof entityId === 'number');
}

/**
 * Converts any string into a unique 32-bit integer. SDBM Is a non-cryptographic
 * hash function that provides reasonable security against collisions. We use this
 * to create unique IDs based on a string input.
 * @param str The string to get the hash for.
 * @returns The numerical hash.
 */
export const sdbmHash = (str: string): number => {
    let hash = 0;

    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + (hash << 6) + (hash << 16) - hash;
    }

    return hash >>> 0;
};

export const moveLayerOnTop = (webMap: WebMap | undefined, layerKey: string) => {
    if (!webMap) {
        return;
    }
    const { layers } = webMap;
    const currentLayer = webMap.findLayerById(layerKey);
    const polylinesLayer = webMap.findLayerById(FEATURE_LAYERS_PROPERTIES.POLYLINES.layerKey);
    layers.reorder(polylinesLayer, layers.length - 1);
    layers.reorder(currentLayer, layers.length - 1);
};

export const setupWebGLContextListeners = (
    mapRef: React.MutableRefObject<HTMLDivElement | null>,
    surface: MapSurface,
    onContextLostCallback?: (event: WebGLContextEvent, canvas: HTMLCanvasElement) => void,
    onContextRestoredCallback?: () => void
): (() => void) => {
    const canvases = mapRef.current?.querySelectorAll('canvas');
    if (!canvases || canvases.length === 0) {
        return () => {};
    }

    const cleanups: (() => void)[] = [];

    canvases.forEach((canvas) => {
        const onContextLost = (event: WebGLContextEvent) => {
            event.preventDefault();
            incrementContextLossCount();
            if (onContextLostCallback) {
                onContextLostCallback(event, canvas);
            } else {
                logMapError({
                    source: 'webgl',
                    event: 'context_lost',
                    surface,
                    extraData: {
                        statusMessage: event.statusMessage,
                        canvasWidth: canvas.width,
                        canvasHeight: canvas.height,
                    },
                });
            }
        };

        const onContextRestored = () => {
            incrementContextRestoreCount();
            logMapInfo({
                source: 'webgl',
                event: 'context_restored',
                surface,
            });
            onContextRestoredCallback?.();
        };

        canvas.addEventListener('webglcontextlost', onContextLost as EventListener);
        canvas.addEventListener('webglcontextrestored', onContextRestored);

        cleanups.push(() => {
            canvas.removeEventListener('webglcontextlost', onContextLost as EventListener);
            canvas.removeEventListener('webglcontextrestored', onContextRestored);
        });
    });

    return () => cleanups.forEach((cleanup) => cleanup());
};
